/**
 * Everyone in a room, a room's Announcements and a circle, as the guest sees them: the pinned line, the
 * lines with "New since you were here", typing, hearts, replies, polls and the call's replay, then the
 * composer. Each reads its place and listens on its wire.
 */
import { type ReactNode, useMemo, useRef, useState } from "react";
import { Face } from "../../components/ui/Face";
import { api } from "../../lib/api";
import { pinWords } from "../../../shared/talk-words";
import type { GuestCircleResponse, GuestEveryoneResponse, TalkAnnouncement, TalkFile, TalkMessage } from "../../../shared/talk";
import { usePage } from "./context";
import { chatClass, Frame, useKept, useMarkSeen, useStick } from "./Frame";
import { AttachmentChips, faceOf, FileChip, Head, Line, longDay, Nav, NextLive, PinBar, PlaceComposer, PostCard, type Quote, ReplayPin, Switch, TypingLine } from "./Parts";
import { callWords, chatLines, offWordsOf, timeOf } from "./talk";
import { typistsOf, useChat, useFeed } from "./useFeed";

const eachDay = new Intl.DateTimeFormat("en-US", { weekday: "short", day: "numeric", month: "short" });

/** The calm line where the composer would be, when the person cannot write here now. */
function CannotPost({ words }: { words: string }): ReactNode {
  const { lay } = usePage();
  return <p className={lay === "phone" ? "jp-cmp-off gq-cmp" : "jp-cmp-off"}>{words}</p>;
}

/** The pinned message of a place, as the bar above its lines. */
function pinnedBar(messages: readonly TalkMessage[]): ReactNode {
  const m = messages.findLast((x) => x.pinned);
  // Why outside the scrolling lines: a chat sticks to its newest line, which would carry the bar out of sight.
  return m ? (
    <div className="jp-pinwrap">
      <PinBar who={m.who.kind === "host" ? m.who.name.split(" ")[0] : m.who.name} text={pinWords(m)} />
    </div>
  ) : null;
}

/** The live call on a phone, one tap away from the chat. */
function LiveBar(): ReactNode {
  const { guest, host, lay } = usePage();
  const live = guest.liveNow;
  if (!live || lay !== "phone") return null;
  return (
    <a className="gq-livebar" href={live.joinPath}>
      <span className="jp-pulse">
        <i />
        Live
      </span>
      <b>{live.circleId ? `${host.first} is live with your circle` : `${host.first} is live`}</b>
      <span className="gq-livebar-b">Join</span>
    </a>
  );
}

interface ChatProps {
  messages: TalkMessage[] | undefined;
  seenAt: number | null | undefined;
  liveChats?: GuestEveryoneResponse["liveChats"];
  chat: ReturnType<typeof useChat>;
  typing: string | null;
  noteTyping: () => void;
  canPost: boolean;
  offWords: string;
  placeholder: string;
  loaded: boolean;
}

/** The lines of a room or circle and its composer. */
function ChatBody({ messages, seenAt, liveChats, chat, typing, noteTyping, canPost, offWords, placeholder, loaded }: ChatProps): ReactNode {
  const { lay, nowMs, host } = usePage();
  const phone = lay === "phone";
  const [quote, setQuote] = useState<(Quote & { id: string }) | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const mark = useKept(placeholder, seenAt);
  const nowSec = Math.floor(nowMs / 1000);
  const lines = useMemo(() => chatLines(chat.messages, mark, nowSec), [chat.messages, mark, nowSec]);
  useStick(body, `${lines.length}:${typing}`);
  return (
    <>
      {pinnedBar(messages ?? [])}
      <div className={chatClass(phone)} ref={body} aria-busy={!loaded}>
        {(liveChats ?? []).map((c) => (
          <p className="jp-sep" key={c.liveSessionId}>
            Chat from the live on {eachDay.format(new Date(c.startsAt * 1000))} · {c.count} {c.count === 1 ? "message" : "messages"}
          </p>
        ))}
        {lines.map((l) =>
          l.type === "sep" ? (
            <p className="jp-sep" key={l.key}>
              {l.day}
            </p>
          ) : l.type === "new" ? (
            <p className="jp-newline" key={l.key}>
              <span>New since you were here</span>
            </p>
          ) : (
            <Line
              key={l.key}
              m={l.message}
              chat={chat}
              hostFirst={host.first}
              failed={chat.failed.has(l.message.clientId ?? "")}
              retry={() => chat.retry(l.message.clientId ?? "")}
              onReply={(m) => setQuote({ id: m.id, who: faceOf(m.who).name || "a member", text: m.body })}
            />
          ),
        )}
        {typing !== null ? <TypingLine name={typing} /> : null}
      </div>
      {canPost ? (
        <PlaceComposer
          placeholder={placeholder}
          noteTyping={noteTyping}
          quote={quote}
          clearQuote={() => setQuote(null)}
          send={(text, attachments) => {
            chat.send(text, attachments, quote?.id);
            setQuote(null);
          }}
        />
      ) : loaded ? (
        <CannotPost words={offWords} />
      ) : null}
    </>
  );
}

/** Faces that fit one row of the column they sit in, ended with "+N" for the rest. */
const FACES_FIT = { phone: 8, upright: 6, side: 7, computer: 7 } as const;

export function Faces({ faces, total }: { faces: { name: string; photoUrl: string | null }[]; total: number }): ReactNode {
  const { lay } = usePage();
  const shown = faces.slice(0, FACES_FIT[lay]);
  const more = Math.max(0, total - shown.length);
  return (
    <div className="jp-online">
      {shown.map((f) => (
        <span key={f.name} className="jp-on" title={f.name}>
          <Face name={f.name} photo={f.photoUrl} size={34} />
        </span>
      ))}
      {more ? <span className="jp-on more">+{more}</span> : null}
    </div>
  );
}

// --- Everyone ---

export function EveryonePlace({ id }: { id: string }): ReactNode {
  const { token, me, place, guest, host, lay, nowMs } = usePage();
  const key = `everyone:${id}`;
  const feed = useFeed<GuestEveryoneResponse>(key, () => api.guestTalk.everyone(token, id), `/api/watch/${encodeURIComponent(token)}/everyone/${encodeURIComponent(id)}/ws`, (d) => typistsOf(d?.messages, host.first));
  const rooms = guest.rooms.find((r) => r.webinarId === id);
  const d = feed.data;
  const chatApi = useMemo(
    () => ({
      send: (req: Parameters<typeof api.guestTalk.sendEveryone>[2]) => api.guestTalk.sendEveryone(token, id, req),
      heart: (mid: string, on: boolean) => api.guestTalk.heart(token, mid, on),
      vote: (mid: string, choice: number) => api.guestTalk.vote(token, mid, choice),
    }),
    [token, id],
  );
  const chat = useChat(d?.messages, me, chatApi, feed.refresh, key);
  useMarkSeen(place, Boolean(d), d?.room.everyoneNew ?? 0, d);
  const room = d?.room ?? rooms;
  const hasPins = (d?.messages ?? []).some((m) => m.pinned && (m.attachments.length > 0 || m.call));
  const online = d?.presence;
  const right = (
    <>
      {online ? (
        <>
          <h3>Here now · {online.onlineCount}</h3>
          <Faces faces={online.online} total={online.onlineCount} />
        </>
      ) : null}
      {hasPins ? <h3>Pinned</h3> : null}
      <PinnedBits messages={d?.messages} />
      <NextLive next={guest.nextLive} />
    </>
  );
  return (
    <Frame
      nav={<Nav title="Everyone" sub={room?.title} />}
      strip={<LiveBar />}
      head={<Head pic={room?.posterUrl} title="Everyone" sub={room?.title ?? ""} />}
      right={right}
    >
      <ChatBody
        messages={d?.messages}
        seenAt={d?.seenAt}
        liveChats={d?.liveChats}
        chat={chat}
        typing={feed.typing}
        noteTyping={feed.noteTyping}
        canPost={d?.canPost ?? false}
        offWords={offWordsOf(d, nowMs)}
        placeholder={lay === "phone" ? "Write to everyone" : "Write to everyone in the room"}
        loaded={Boolean(d)}
      />
    </Frame>
  );
}

/** A replay and the files that are pinned in a place. */
function PinnedBits({ messages }: { messages: readonly TalkMessage[] | undefined }): ReactNode {
  const { playTalk } = usePage();
  const pinned = (messages ?? []).filter((m) => m.pinned);
  return (
    <>
      {pinned
        .filter((m) => m.call)
        .map((m) => (
          <ReplayPin
            key={m.id}
            label="The call's replay"
            title={m.call?.title ?? ""}
            poster={m.call?.faces[0]?.photoUrl ?? null}
            line={callWords(m.createdAt, m.call?.minutes ?? 0)}
            onClick={() => playTalk(m.call?.webinarId ?? "", { sessionId: m.call?.liveSessionId, start: true })}
          />
        ))}
      <AttachmentChips list={pinned.flatMap((m) => m.attachments)} />
    </>
  );
}

// --- Announcements ---

function sortPosts(posts: readonly TalkAnnouncement[]): TalkAnnouncement[] {
  // Why date order only: the right side already shows what is pinned, and the host's list reads the same way.
  return posts.toSorted((a, b) => a.createdAt - b.createdAt);
}

export function AnnouncePlace({ id }: { id: string }): ReactNode {
  const { token, place, guest, host, goPlace, setReplying, playTalk, lay } = usePage();
  const key = `announce:${id}`;
  // Why the room's wire: Everyone and Announcements are one room's, and a post pokes it.
  const feed = useFeed(key, () => api.guestTalk.announcements(token, id), `/api/watch/${encodeURIComponent(token)}/everyone/${encodeURIComponent(id)}/ws`);
  const [hearted, setHearted] = useState<ReadonlyMap<string, boolean>>(new Map());
  const rooms = guest.rooms.find((r) => r.webinarId === id);
  const d = feed.data;
  useMarkSeen(place, Boolean(d), rooms?.announceNew ?? 0);
  const posts = useMemo(
    () =>
      sortPosts(d?.posts ?? []).map((p) => {
        const on = hearted.get(p.id);
        return on === undefined || on === p.hearted ? p : { ...p, hearted: on, hearts: Math.max(0, p.hearts + (on ? 1 : -1)) };
      }),
    [d?.posts, hearted],
  );
  const body = useRef<HTMLDivElement>(null);
  useStick(body, `${posts.length}:${id}`);
  const heart = (p: TalkAnnouncement) => {
    const was = p.hearted;
    setHearted((h) => new Map(h).set(p.id, !was));
    api.guestTalk.heartPost(token, p.id, !was).catch(() => setHearted((h) => new Map(h).set(p.id, was)));
  };
  const reply = (p: TalkAnnouncement) => {
    setReplying({ kind: "announcement", post: { id: p.id, body: p.body }, webinar_id: id });
    goPlace({ kind: "direct" });
  };
  const room = d?.room ?? rooms;
  const pinned = posts.filter((p) => p.pinned && (p.body || p.replay || p.attachments.length > 0));
  const right = (
    <>
      <NextLive next={guest.nextLive} />
      {pinned.length ? <h3>Pinned</h3> : null}
      {pinned.map((p) => (
        <div className="jp-pinpost" key={p.id}>
          {p.body ? <p>{p.body}</p> : null}
          {p.replay ? (
            <ReplayPin
              title={p.replay.title}
              poster={p.replay.posterUrl}
              line={callWords(p.replay.startsAt, p.replay.minutes)}
              label="The replay"
              onClick={() => playTalk(id, { sessionId: p.replay?.liveSessionId, start: true })}
            />
          ) : null}
          <AttachmentChips list={p.attachments} />
        </div>
      ))}
    </>
  );
  return (
    <Frame nav={<Nav title="Announcements" sub={`From ${host.first} · ${room?.title ?? ""}`} />} head={<Head icon="bell" title="Announcements" sub={`From ${host.first} · ${room?.title ?? ""}`} />} right={right}>
      <div className={chatClass(lay === "phone", true)} ref={body} aria-busy={!d}>
        {d && !posts.length ? <p className="jp-empty">Nothing posted yet.</p> : null}
        {posts.map((p) => (
          <PostCard key={p.id} p={p} onHeart={() => heart(p)} onReply={() => reply(p)} />
        ))}
      </div>
    </Frame>
  );
}

// --- a circle ---

export function CirclePlace({ id }: { id: string }): ReactNode {
  const { token, me, place, guest, host, lay, nowMs, openCall } = usePage();
  const key = `circle:${id}`;
  const feed = useFeed<GuestCircleResponse>(key, () => api.guestTalk.circle(token, id), `/api/watch/${encodeURIComponent(token)}/circles/${encodeURIComponent(id)}/ws`, (d) => typistsOf(d?.messages, host.first));
  const [coming, setComing] = useState<boolean | null>(null);
  const d = feed.data;
  const chatApi = useMemo(
    () => ({
      send: (req: Parameters<typeof api.guestTalk.sendCircle>[2]) => api.guestTalk.sendCircle(token, id, req),
      heart: (mid: string, on: boolean) => api.guestTalk.heart(token, mid, on),
      vote: (mid: string, choice: number) => api.guestTalk.vote(token, mid, choice),
    }),
    [token, id],
  );
  const chat = useChat(d?.messages, me, chatApi, feed.refresh, key);
  const channel = guest.rooms.flatMap((r) => r.circles).find((c) => c.circleId === id);
  useMarkSeen(place, Boolean(d), channel?.newCount ?? 0);
  const next = d?.nextCall ?? null;
  const going = coming ?? next?.coming ?? false;
  const rsvp = (on: boolean) => {
    setComing(on);
    api.guestTalk.rsvp(token, id, on).catch(() => setComing(null));
  };
  const nextLine = next ? `${eachDay.format(new Date(next.startsAt * 1000))}, ${timeOf(next.startsAt)}` : "";
  // Why a tappable line below the side-by-side sizes: a card there would cover the chat's first lines.
  const tapped = lay === "phone" || lay === "upright";
  const callLine =
    next && tapped ? (
      <button type="button" className="jp-calllink" onClick={() => openCall({ circleId: id, name: d?.circle.name ?? channel?.name ?? "", next })}>
        Next call {nextLine}
      </button>
    ) : null;
  const members = d?.members ?? [];
  const total = d?.circle.members ?? members.length;
  const circleFiles = (d?.pinnedFiles ?? []).filter((f) => f.url);
  const right = (
    <>
      {next && !tapped ? (
        <div className="jp-next">
          <small>Next call</small>
          <b>{longDay(next.startsAt)}</b>
          <span>
            {timeOf(next.startsAt)} · {next.lengthMin} min
          </span>
          <div className="jp-set flat">
            <span>
              <b>I am coming</b>
            </span>
            <Switch on={going} label="I am coming" onChange={rsvp} />
          </div>
        </div>
      ) : null}
      {d ? (
        <>
          <h3>In the circle · {total}</h3>
          <Faces faces={members} total={total} />
        </>
      ) : null}
      {circleFiles.length ? <h3>Pinned</h3> : null}
      {circleFiles.map((f: TalkFile) => (
        <FileChip key={f.key} f={f} />
      ))}
    </>
  );
  const title = d?.circle.name ?? channel?.name ?? "";
  const sub = callLine ? (
    <>
      {total} people · {callLine}
    </>
  ) : (
    `${total} people`
  );
  return (
    <Frame
      nav={<Nav title={title} sub={callLine ?? `${total} people`} />}
      strip={<LiveBar />}
      head={<Head icon="target" title={title} sub={sub} />}
      right={right}
    >
      <ChatBody
        messages={d?.messages}
        seenAt={d?.seenAt}
        chat={chat}
        typing={feed.typing}
        noteTyping={feed.noteTyping}
        canPost={d?.canPost ?? false}
        offWords={offWordsOf(d, nowMs)}
        placeholder="Write to the circle"
        loaded={Boolean(d)}
      />
    </Frame>
  );
}

